import { useState } from "react";

import { ActionIcon, Alert, AlertProps, Group, List, Stack, Tooltip } from "@mantine/core";

import { IconExclamationCircle, IconRefresh } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";

import { HealthGorillaRequiredFields } from "../types/labs";

interface HealthGorillaRequiredFieldsAlertProps extends AlertProps {
  requiredFields: HealthGorillaRequiredFields;
  isRefreshing: boolean;
  refreshPatient: () => Promise<void>;
}

export default function HealthGorillaRequiredFieldsAlert({
  requiredFields,
  isRefreshing,
  refreshPatient,
  ...props
}: HealthGorillaRequiredFieldsAlertProps) {
  const [isClosed, setIsClosed] = useState(false);

  return (
    <Alert
      radius="sm"
      color="yellow"
      withCloseButton
      hidden={isClosed}
      closeButtonLabel="Close"
      onClose={() => setIsClosed(true)}
      styles={{ title: { fontSize: "var(--mantine-font-size-md)" } }}
      icon={<IconExclamationCircle size={16} color="var(--mantine-color-orange-8)" />}
      title={
        <Group gap="xs" wrap="nowrap" align="center">
          Lab ordering unavailable for this client
          <Tooltip withArrow label="Refresh required fields status">
            <ActionIcon
              size="sm"
              color="yellow"
              variant="subtle"
              disabled={isRefreshing}
              aria-busy={isRefreshing}
              aria-label="Refresh required fields status"
              data-testid="healthGorillaRequiredFieldsRefresh"
              onClick={(event) => {
                event.stopPropagation();
                if (isRefreshing) {
                  return;
                }
                void refreshPatient();
              }}
            >
              <IconRefresh size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
      {...props}
    >
      <TextWrapper mt="xs">
        The following client information is required to order labs:
        <List mt="md" withPadding>
          <Stack gap="xs">
            {!requiredFields.hasFullName && (
              <List.Item>
                <strong>Full Name</strong> (Client Profile &rarr; Identifying Information)
              </List.Item>
            )}
            {!requiredFields.hasBirthDate && (
              <List.Item>
                <strong>Date of Birth (DOB)</strong> (Client Profile &rarr; Identifying Information)
              </List.Item>
            )}
            {!requiredFields.hasSexAssignedAtBirth && (
              <List.Item>
                <strong>Sex Assigned at Birth</strong> (Client Profile &rarr; Demographic)
              </List.Item>
            )}
            {!requiredFields.hasLegalSex && (
              <List.Item>
                <strong>Legal Sex</strong> (Client Profile &rarr; Demographic)
              </List.Item>
            )}
            {!requiredFields.hasLanguages && (
              <List.Item>
                <strong>Language</strong> (Client Profile &rarr; Demographic)
              </List.Item>
            )}
            {!requiredFields.hasPhoneNumber && (
              <List.Item>
                <strong>Phone Number</strong> (Client Profile &rarr; Contact Information). At least
                one <em>Home</em> or <em>Mobile</em> phone number is required.
              </List.Item>
            )}
            {!requiredFields.hasAddress && (
              <List.Item>
                <strong>Full Address</strong> (Client Profile &rarr; Contact Information)
              </List.Item>
            )}
          </Stack>
        </List>
      </TextWrapper>
    </Alert>
  );
}
